import { Component, type ReactNode } from "react";
import { messages } from "../i18n";
import { captureError } from "../lib/observability";
import styles from "./ErrorBoundary.module.css";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level React error boundary, wrapping `<App />` in `main.tsx`.
 *
 * A class component because `componentDidCatch` / `getDerivedStateFromError`
 * have no hook equivalent — this is the one place in the app that must be a
 * class. It catches render-phase errors thrown anywhere below it, reports
 * them to Sentry via `captureError` (never with an extra/context payload —
 * see `lib/observability.ts` for why), and renders a minimal fallback with
 * a retry button that resets local state so the tree remounts.
 *
 * `componentDidCatch` does NOT catch errors in event handlers, async
 * callbacks, or effects — those already flow through `lib/log.ts`'s
 * `logError` (which forwards to `captureError` too) at their own call
 * sites.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    captureError(error);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div className={styles.fallback}>
        <button type="button" className={styles.retryButton} onClick={this.handleRetry}>
          {messages.pairing.tryAgain}
        </button>
      </div>
    );
  }
}
